import React from 'react';

import {
    ACTION_NAMES, GamePlayerStatus, Texture,
} from '../../../Constants';
import { getTextureUrl } from '../../TwoD/Common/TextureTools';

import './CurrentAction.css';

// Map of GamePlayerStatus juiced-sub-state → action_id. The banner
// surfaces ONLY for these states — once the player has committed
// to an action and dropped out of TAKING_TURN_ID into the per-
// action state machine. During the picking phases (CHOOSE_ACTIONS,
// SELECT_ACTION) the hex / picker UI is the source of truth for
// what the player is *considering*; the banner would be premature
// chrome there ("Renovation" shown while still picking) and was
// flagged 2026-05-25 as actively misleading.
const STATUS_TO_ACTION_ID = {
    [GamePlayerStatus.RESEARCHING_ID]: 1,
    [GamePlayerStatus.LODGING_ID]: 2,
    [GamePlayerStatus.ROBOTING_ID]: 3,
    [GamePlayerStatus.RENOVATING_ID]: 4,
    [GamePlayerStatus.ADVERTISING_ID]: 5,
    [GamePlayerStatus.GHOSTING_ID]: 6,
};

// Actions whose sub-flow has an intermediate two-step selection
// (Renovation: pick station/storage tile, then destination;
// Advertisement: pick orbit card, then dock/discard/target). For
// these the player can land on a first pick they want to undo
// without committing. Other juiced sub-states resolve atomically
// from a single click and don't need a clear affordance.
const CLEARABLE_ACTION_IDS = new Set([4, 5]);

// Fired when the player clicks Clear next to the current action
// pill. Listened to by RenovationController and
// AdvertisementPossibilities, which reset their in-controller
// selection state and re-publish the initial highlight set so the
// player can pick again from scratch. No engine round-trip — the
// selection is purely a client-side intermediate state until the
// player commits via the second click.
export const CLEAR_ACTION_SELECTION_EVENT = 'clear-action-selection-event';

// Resolve which action_id is "current" given the player state.
// Returns a single-entry list for juiced sub-states (the player
// has committed; this is what they're doing right now) or empty
// for anything else.
export function currentActionIds(gamePlayerData) {
    if (!gamePlayerData) return [];
    const fromStatus = STATUS_TO_ACTION_ID[gamePlayerData.status];
    if (fromStatus) return [fromStatus];
    return [];
}

// CurrentAction — a thin banner showing the action(s) the player
// has committed to. Renders nothing if there's no current action
// to display (the empty-array case). Used inside ActionWindow when
// the hex is hidden — 'select' mode (player picked their Focus/
// Diversify and is now firing actions) and the juiced sub-states
// (player has dropped into the per-action state machine and the
// Lodging / Research / Ghost / etc. window owns the real surface).
function onClearClick() {
    window.dispatchEvent(new Event(CLEAR_ACTION_SELECTION_EVENT));
}

export function CurrentAction({ gamePlayerData }) {
    const actionIds = currentActionIds(gamePlayerData);
    if (actionIds.length === 0) return null;
    const showClear = actionIds.some(
        (actionId) => CLEARABLE_ACTION_IDS.has(actionId));
    return (
        <div className={'current-action'}
             data-testid={'current-action'}>
            {actionIds.map((actionId, i) => (
                <span key={'a-' + actionId + '-' + i}
                      className={'current-action__pill'}
                      data-action-id={actionId}>
                    <img className={'current-action__icon'}
                         src={getTextureUrl(Texture.ACTIONS[actionId])}
                         data-tname={Texture.ACTIONS[actionId]}
                         alt={ACTION_NAMES[actionId]}/>
                    <span className={'current-action__name'}>
                        {ACTION_NAMES[actionId]}
                    </span>
                </span>
            ))}
            {showClear && (
                <button type={'button'}
                        className={'current-action__clear'}
                        data-testid={'current-action-clear'}
                        onClick={onClearClick}>
                    Clear
                </button>
            )}
        </div>
    );
}

export default CurrentAction;
